import type { Message } from '../../../i18n/types';

/**
 * Texts of the test case ↔ autotest links: the «Autotests» card of a case, the
 * cases next to classes in Automation and the import from description lines.
 */
export const automationCaseLinksEn = {
  'automation.caseLinks.title': 'Autotests',
  'automation.caseLinks.hint':
    'Which autotest covers this case and how it went in the latest run. A link is picked here or written in the autotest as @TmsLink("KEY"); the repositories are rescanned every night.',
  'automation.caseLinks.none': 'No autotest is linked yet.',
  'automation.caseLinks.add': 'Link an autotest',
  'automation.caseLinks.link': 'Link',
  'automation.caseLinks.linked': 'Autotest linked',
  'automation.caseLinks.linkTitle': 'Link an autotest to {key}',
  'automation.caseLinks.linkHint':
    'Classes and tests come from the last repository scan. The case becomes «automated».',
  'automation.caseLinks.searchLabel': 'Find an autotest',
  'automation.caseLinks.searchPlaceholder': 'Test name, class or repository',
  'automation.caseLinks.searchStart':
    'Type words of the test name — «QR is paid» — or a class or repository name. Every scanned project is searched.',
  'automation.caseLinks.searchNone':
    'Nothing found. A test shows up here after its repository is scanned.',
  'automation.caseLinks.searchResults': 'Autotests found',
  'automation.caseLinks.searchMore': 'The first {count} are shown — narrow the query.',
  'automation.caseLinks.alreadyLinked': 'already linked',
  'automation.caseLinks.gone': 'not in the code',
  'automation.caseLinks.goneHint.class':
    'The latest repository scan did not find this class: it was renamed or deleted. Unlink it and link the right test — autotests of a run skip it.',
  'automation.caseLinks.goneHint.test':
    'The latest repository scan did not find this test in the class: it was renamed or deleted. Unlink it and link the right test — autotests of a run skip it.',
  'automation.caseLinks.toCase': 'Link to a case',
  'automation.caseLinks.toCaseTitle': 'Link «{name}» to a test case',
  'automation.caseLinks.toCaseHint':
    'The case becomes «automated», and a run with this case starts the autotest. In the code the same link is @TmsLink("KEY").',
  'automation.caseLinks.what': 'What covers the case',
  'automation.caseLinks.caseSearchLabel': 'Find a test case',
  'automation.caseLinks.caseSearchPlaceholder': 'Case key or title',
  'automation.caseLinks.caseSearchStart': 'Type a key — PNT-22 — or words of the title.',
  'automation.caseLinks.caseSearchNone': 'No such case.',
  'automation.caseLinks.caseSearchResults': 'Test cases found',
  'automation.caseLinks.automated': 'automated',
  'automation.caseLinks.linkedTo': 'Linked to {key}',
  'automation.caseLinks.wholeClass': 'The whole class',
  'automation.caseLinks.fromCode': 'from code',
  'automation.caseLinks.fromCodeHint': '@TmsLink in the autotest; change it in the code and rescan',
  'automation.caseLinks.manual': 'linked here',
  'automation.caseLinks.manualHint': 'Linked by {who}',
  'automation.caseLinks.remove': 'Unlink',
  'automation.caseLinks.removeTitle': 'Unlink the autotest?',
  'automation.caseLinks.removeHint':
    '{name} will no longer be shown for this case. The «automated» flag stays as it is.',
  'automation.caseLinks.noRuns': 'Not in any finished run yet',
  'automation.caseLinks.counts': '{passed} of {total} passed',
  'automation.caseLinks.result.passed': 'Passed',
  'automation.caseLinks.result.failed': 'Failed',
  'automation.caseLinks.result.broken': 'Broken',
  'automation.caseLinks.result.skipped': 'Skipped',
  'automation.caseLinks.result.missing': 'Not in the last report',
  'automation.caseLinks.casesOf': 'Test cases',
  'automation.caseLinks.scanLinked': {
    one: '@TmsLink: {count} case linked',
    other: '@TmsLink: {count} cases linked',
  },
  'automation.caseLinks.scanUnknown': 'No such cases in TMS: {keys}',
  'automation.caseLinks.scanConverted': {
    one: '{count} link picked by hand is now from the code',
    other: '{count} links picked by hand are now from the code',
  },
  'automation.caseLinks.scanGone': {
    one: '{count} link picked by hand leads to a test that is not in the code',
    other: '{count} links picked by hand lead to tests that are not in the code',
  },
  'automation.caseLinks.fromDescriptions': 'Links from descriptions',
  'automation.caseLinks.fromDescriptionsTitle': 'Turn «Автотесты в GitLab» lines into links?',
  'automation.caseLinks.fromDescriptionsHint':
    'Cases whose description names autotests in a «Автотесты в GitLab» line get real links. Nothing is written until you confirm.',
  'automation.caseLinks.fromDescriptionsEmpty': 'No case description has such a line.',
  'automation.caseLinks.fromDescriptionsApply': 'Create links',
  'automation.caseLinks.fromDescriptionsDone': {
    one: '{count} link created',
    other: '{count} links created',
  },
  'automation.caseLinks.unmatched': 'Not matched: {names}',
} as const;

export const automationCaseLinksRu: Record<keyof typeof automationCaseLinksEn, Message> = {
  'automation.caseLinks.title': 'Автотесты',
  'automation.caseLinks.hint':
    'Какой автотест покрывает кейс и как он прошёл в последнем прогоне. Связь ставится здесь или в коде автотеста: @TmsLink("КЛЮЧ"); репозитории пересканируются каждую ночь.',
  'automation.caseLinks.none': 'Автотест ещё не привязан.',
  'automation.caseLinks.add': 'Привязать автотест',
  'automation.caseLinks.link': 'Привязать',
  'automation.caseLinks.linked': 'Автотест привязан',
  'automation.caseLinks.linkTitle': 'Привязать автотест к {key}',
  'automation.caseLinks.linkHint':
    'Классы и тесты — из последнего сканирования репозитория. Кейс станет «Автоматизирован».',
  'automation.caseLinks.searchLabel': 'Найти автотест',
  'automation.caseLinks.searchPlaceholder': 'Название теста, класс или репозиторий',
  'automation.caseLinks.searchStart':
    'Наберите слова из названия теста — «QR оплачивается» — или имя класса, репозитория. Ищем во всех просканированных проектах.',
  'automation.caseLinks.searchNone':
    'Ничего не нашлось. Тест появится здесь после сканирования его репозитория.',
  'automation.caseLinks.searchResults': 'Найденные автотесты',
  'automation.caseLinks.searchMore': 'Показаны первые {count} — уточните запрос.',
  'automation.caseLinks.alreadyLinked': 'уже привязан',
  'automation.caseLinks.gone': 'нет в коде',
  'automation.caseLinks.goneHint.class':
    'При последнем сканировании репозитория этого класса не нашлось: его переименовали или удалили. Отвяжите и привяжите нужный тест — автотесты прогона его пропускают.',
  'automation.caseLinks.goneHint.test':
    'При последнем сканировании репозитория этого теста в классе не нашлось: его переименовали или удалили. Отвяжите и привяжите нужный тест — автотесты прогона его пропускают.',
  'automation.caseLinks.toCase': 'Привязать к кейсу',
  'automation.caseLinks.toCaseTitle': 'Привязать «{name}» к тест-кейсу',
  'automation.caseLinks.toCaseHint':
    'Кейс станет «Автоматизирован», и прогон с этим кейсом запустит автотест. В коде та же связь — @TmsLink("КЛЮЧ").',
  'automation.caseLinks.what': 'Что покрывает кейс',
  'automation.caseLinks.caseSearchLabel': 'Найти тест-кейс',
  'automation.caseLinks.caseSearchPlaceholder': 'Ключ или название кейса',
  'automation.caseLinks.caseSearchStart': 'Наберите ключ — PNT-22 — или слова из названия.',
  'automation.caseLinks.caseSearchNone': 'Такого кейса нет.',
  'automation.caseLinks.caseSearchResults': 'Найденные тест-кейсы',
  'automation.caseLinks.automated': 'автоматизирован',
  'automation.caseLinks.linkedTo': 'Привязан к {key}',
  'automation.caseLinks.wholeClass': 'Весь класс',
  'automation.caseLinks.fromCode': 'из кода',
  'automation.caseLinks.fromCodeHint':
    '@TmsLink в автотесте: меняется в коде и после пересканирования',
  'automation.caseLinks.manual': 'привязан здесь',
  'automation.caseLinks.manualHint': 'Привязал {who}',
  'automation.caseLinks.remove': 'Отвязать',
  'automation.caseLinks.removeTitle': 'Отвязать автотест?',
  'automation.caseLinks.removeHint':
    '{name} больше не будет показан у кейса. Флаг «Автоматизирован» останется как есть.',
  'automation.caseLinks.noRuns': 'Ещё не был ни в одном завершённом прогоне',
  'automation.caseLinks.counts': 'прошло {passed} из {total}',
  'automation.caseLinks.result.passed': 'Прошёл',
  'automation.caseLinks.result.failed': 'Упал',
  'automation.caseLinks.result.broken': 'Сломан',
  'automation.caseLinks.result.skipped': 'Пропущен',
  'automation.caseLinks.result.missing': 'Нет в последнем отчёте',
  'automation.caseLinks.casesOf': 'Тест-кейсы',
  'automation.caseLinks.scanLinked': {
    one: '@TmsLink: привязан {count} кейс',
    few: '@TmsLink: привязано {count} кейса',
    many: '@TmsLink: привязано {count} кейсов',
    other: '@TmsLink: привязано {count} кейса',
  },
  'automation.caseLinks.scanUnknown': 'Таких кейсов в TMS нет: {keys}',
  'automation.caseLinks.scanConverted': {
    one: '{count} связь, привязанная вручную, теперь из кода',
    few: '{count} связи, привязанные вручную, теперь из кода',
    many: '{count} связей, привязанных вручную, теперь из кода',
    other: '{count} связи, привязанные вручную, теперь из кода',
  },
  'automation.caseLinks.scanGone': {
    one: '{count} связь, привязанная вручную, ведёт на тест, которого нет в коде',
    few: '{count} связи, привязанные вручную, ведут на тесты, которых нет в коде',
    many: '{count} связей, привязанных вручную, ведут на тесты, которых нет в коде',
    other: '{count} связи, привязанные вручную, ведут на тесты, которых нет в коде',
  },
  'automation.caseLinks.fromDescriptions': 'Связи из описаний',
  'automation.caseLinks.fromDescriptionsTitle': 'Превратить строки «Автотесты в GitLab» в связи?',
  'automation.caseLinks.fromDescriptionsHint':
    'Кейсы, у которых в описании автотесты названы строкой «Автотесты в GitLab», получат настоящие связи. Пока вы не подтвердите, ничего не записывается.',
  'automation.caseLinks.fromDescriptionsEmpty': 'Ни в одном описании такой строки нет.',
  'automation.caseLinks.fromDescriptionsApply': 'Создать связи',
  'automation.caseLinks.fromDescriptionsDone': {
    one: 'Создана {count} связь',
    few: 'Создано {count} связи',
    many: 'Создано {count} связей',
    other: 'Создано {count} связи',
  },
  'automation.caseLinks.unmatched': 'Не сопоставлено: {names}',
};
